import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useLocale } from 'next-intl';

import { client } from '@/shared/api/client/base';
import {
  BoardApi,
  BoardApiBoardGetRequest,
  BoardInsertModel,
  BoardInsertRequest,
  BoardListSearchModel,
} from '@/shared/api/model';

import {
  BoardApiBoardPostIdGetRequest,
  BoardDetailSearchModel,
  BoardDownloadModel,
  BoardDownloadRequest,
  BoardVerifyPasswordRequest,
} from './../../../shared/api/model/api';

/**
 * @description
 * header의 Accept-Language 헤더 설정은 BoardApi 클래스에서 처리해주고 있습니다.
 * generate-api.js로 생성된 코드에서 필요한 값을 요청하고 있으니 추가하지 않아도 괜찮습니다.
 *
 * 혹시 오류가 있으면 추가 수정 후 공유하시거나, 알려주시기 바랍니다.
 */

const boardApi = new BoardApi(
  {
    baseOptions: {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
    isJsonMime: () => true,
  },
  process.env.NEXT_PUBLIC_SERVICE_API_URL,
  /**
   * @description
   * base로 정의된 axios 인스턴스 설정을 가져와 boardApi 인스턴스 생성에 주입하고 있습니다.
   */
  axios.create({
    ...client.defaults,
    baseURL: process.env.NEXT_PUBLIC_SERVICE_API_URL,
  }),
);

/**
 * @description
 * 훅 내부에서 언어 처리를 자동 지원하고 있어, Omit<>으로 Accept_Language, langType을 제외한 값만 외부에서 받습니다.
 * BoardApiBoardGetRequest 타입은 boardGet 메서드의 파라미터 타입입니다.
 */
export const useGetBoard = (
  query: Omit<BoardApiBoardGetRequest, 'Accept_Language'>,
  queryKey: Array<string>,
) => {
  /**
   * @description
   * 현재 사용자 언어를 clientside에서 가져와 사용합니다.
   * (clientSide에서 언어 변경 시 반영)
   */
  const language = useLocale();

  return useQuery<BoardListSearchModel>({
    queryKey: queryKey,
    queryFn: async () => {
      const response = await boardApi.boardGet({
        ...query,
        Accept_Language: language,
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const usePostBoard = (
  onSuccess?: (data: BoardInsertModel) => void,
  onError?: (error: Error) => void,
) => {
  const language = useLocale();

  return useMutation<BoardInsertModel, Error, Omit<BoardInsertRequest, 'isPasswordSet'>>({
    mutationFn: async (req: Omit<BoardInsertRequest, 'isPasswordSet'>) => {
      try {
        const response = await boardApi.boardPost({
          Accept_Language: language,
          BoardInsertRequest: { ...req, isPasswordSet: true },
        });
        return response.data;
      } catch (error) {
        console.error('Error occurred at postBoard', error);
        return {};
      }
    },
    onSuccess,
    onError,
  });
};

export const useGetBoardDetail = (
  query: Omit<BoardApiBoardPostIdGetRequest, 'Accept_Language'>,
  queryKey: Array<string>,
) => {
  const language = useLocale();

  return useQuery<BoardDetailSearchModel>({
    queryKey: queryKey,
    queryFn: async () => {
      const response = await boardApi.boardPostIdGet({
        ...query,
        Accept_Language: language,
      });
      return response.data;
    },
  });
};

export const useGetBoardDetailMutation = (
  onSuccess?: (data: BoardDetailSearchModel) => void,
  onError?: (error: Error) => void,
) => {
  const language = useLocale();

  return useMutation<
    BoardDetailSearchModel,
    Error,
    Omit<BoardApiBoardPostIdGetRequest, 'Accept_Language'>
  >({
    mutationFn: async (req: Omit<BoardApiBoardPostIdGetRequest, 'Accept_Language'>) => {
      {
        try {
          const response = await boardApi.boardPostIdGet({
            Accept_Language: language,
            postId: req.postId,
          });
          return response.data;
        } catch (error) {
          console.error('Error occurred at boardPostIdGet', error);
          return {};
        }
      }
    },
    onSuccess,
    onError,
  });
};

export const usePostVerifyQnaPassword = (
  onSuccess?: (data: boolean) => void,
  onError?: (error: Error) => void,
) => {
  const language = useLocale();

  return useMutation<boolean, Error, BoardVerifyPasswordRequest>({
    mutationFn: async (req: BoardVerifyPasswordRequest) => {
      {
        try {
          const response = await boardApi.boardVerifyPasswordPost({
            Accept_Language: language,
            BoardVerifyPasswordRequest: req,
          });
          return response.data;
        } catch (error) {
          console.error('Error occurred at verifyPassword', error);
          return false;
        }
      }
    },
    onSuccess,
    onError,
  });
};

export const usePostBoardDownload = (
  onSuccess?: (data: BoardDownloadModel) => void,
  onError?: (error: Error) => void,
) => {
  const language = useLocale();

  return useMutation<BoardDownloadModel, Error, BoardDownloadRequest>({
    mutationFn: async (req: BoardDownloadRequest) => {
      {
        try {
          const response = await boardApi.boardDownloadPost(
            {
              Accept_Language: language,
              path: req.path,
            },
            {
              responseType: 'blob',
            },
          );
          const contentType = response.headers['content-type'];
          return {
            blobData: response.data as unknown as Blob,
            contentType,
          };
        } catch (error) {
          console.error('Error occurred at verifyPassword', error);
          throw new Error('파일 다운로드 실패');
        }
      }
    },
    onSuccess,
    onError,
  });
};
